/**
 * The page description the chat widget receives for a questionnaire step.
 *
 * The description is built only from the static title and subtitle the
 * questionnaire already shows on that step, so the assistant knows what the
 * visitor is looking at without receiving anything the visitor entered. A
 * sentence about how far the step is from an offer to schedule a free
 * consultation follows, when schedulingNotes.ts has one for the step.
 */

import { choiceAndOpenStepContent } from '@/content/choiceAndOpenStepContent';
import { Branch, isBranchGuard } from '@/state/state.types';
import { getStepName } from '@/utils/stepUtils';

import { describeScheduling } from './schedulingNotes';

/** The chat service rejects a page description longer than this many characters. */
export const MAX_PAGE_DESCRIPTION_LENGTH = 600;

const collapseWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

// Text that would exceed `limit` characters is cut at the last whole word that
// fits and ends with an ellipsis, so the result is always within the limit.
const fitToLimit = (text: string, limit: number) => {
  if (text.length <= limit) return text;
  const room = text.slice(0, limit - 1);
  const lastSpace = room.lastIndexOf(' ');
  return `${lastSpace > 0 ? room.slice(0, lastSpace) : room}…`;
};

/**
 * Describe the questionnaire step `stepId` for the chat assistant, or return
 * undefined when the step has neither static text nor a scheduling note.
 *
 * The title is used only when it is a plain string. A title written as a
 * function renders values derived from the visitor's answers, such as an
 * income threshold computed from their household size, so it is left out and
 * the subtitle is used alone. The scheduling note comes last and is never cut;
 * when the whole description would be too long, the step's own text is
 * shortened instead. The result never contains more than
 * MAX_PAGE_DESCRIPTION_LENGTH characters.
 */
export const describeStep = (stepId: string | undefined): string | undefined => {
  if (!stepId) return undefined;
  const stepName = getStepName(stepId);
  if (!stepName) return undefined;

  const branches = stepId.split('.').filter(isBranchGuard);
  const content = [...branches, Branch.default]
    .map((branch) => choiceAndOpenStepContent[stepName]?.[branch])
    .find(Boolean);

  const title = typeof content?.title === 'string' ? collapseWhitespace(content.title) : '';
  const subtitle = content?.subtitle ? collapseWhitespace(content.subtitle) : '';

  const sentences: string[] = [];
  if (title) sentences.push(`The visitor is on the questionnaire step "${title}".`);
  if (subtitle) {
    sentences.push(
      title
        ? `The page says: "${subtitle}"`
        : `The visitor is on a questionnaire step where the page says: "${subtitle}"`,
    );
  }
  const scheduling = describeScheduling(stepName);

  if (!scheduling) {
    return sentences.length > 0
      ? fitToLimit(sentences.join(' '), MAX_PAGE_DESCRIPTION_LENGTH)
      : undefined;
  }
  if (sentences.length === 0) return fitToLimit(scheduling, MAX_PAGE_DESCRIPTION_LENGTH);
  const room = MAX_PAGE_DESCRIPTION_LENGTH - scheduling.length - 1;
  return `${fitToLimit(sentences.join(' '), room)} ${scheduling}`;
};
